"use client";

import type React from "react";
import { useState } from "react";

import { Button } from "@ala/core/components/button";
import { ModalActions, ModalContent, ModalHeader, ModalLayout, useModalContext } from "@ala/core/components/modal";
import { clsx } from "@ala/core/utils/clsx";
import { TrashWarningIcon } from "@ala/icons/office/solid-dual";
import { CancelIcon, CheckIcon } from "@ala/icons/symbols/solid";

import { Checkbox } from "../checkbox/checkbox";
import { ModalBanner, type ModalCloseOutcome } from "../modal";

export interface ConfirmationModalProps {
  title: string;
  description: string;
  variant?: "red" | "contrast";
  content?: React.ReactNode;
  iconClassName?: string;
  icon?: React.ReactNode;
  submitText: string;
  submitIcon?: React.ReactNode;
  cancelText?: string;
  cancelIcon?: React.ReactNode;
  checkboxContent?: string;
  onSubmit: () => void | ModalCloseOutcome | Promise<void | ModalCloseOutcome>;
}

export const ConfirmationModal = ({
  title = "Are you sure?",
  description = "Confirm action",
  submitIcon = <CheckIcon />,
  submitText = "Confirm",
  cancelText = "Cancel",
  cancelIcon = <CancelIcon />,
  variant = "red",
  content,
  iconClassName,
  icon = (
    <TrashWarningIcon
      primary="text-contrast-50"
      secondary="text-contrast-900"
      className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
    />
  ),
  checkboxContent,
  onSubmit,
}: ConfirmationModalProps) => {
  const { closeModal } = useModalContext();
  const [isChecked, setIsChecked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    setIsSubmitting(true);

    const outcome = await onSubmit();
    if (typeof outcome !== "object" || !outcome.shouldKeepModalOpen) {
      closeModal();
    }

    setIsSubmitting(false);

    return false;
  };

  return (
    <ModalLayout as="form" className="w-screen sm:w-screen sm:max-w-[653px]" onSubmit={handleSubmit}>
      <ModalHeader>
        <ModalBanner
          title={title}
          subtitle={description}
          icon={icon}
          iconClassName={clsx("bg-contrast-900 text-[40px]", variant === "red" && "bg-red-500", iconClassName)}
        />
      </ModalHeader>

      <ModalContent className="relative text-sm text-contrast-600">
        {content}

        {checkboxContent && (
          <Checkbox className="mt-8 justify-self-start" checked={isChecked} onCheckedChange={(details) => setIsChecked(!!details.checked)}>
            {checkboxContent}
          </Checkbox>
        )}
      </ModalContent>

      <ModalActions className="grid grid-cols-2">
        <Button variant="light" size="lg" onClick={() => closeModal()}>
          {cancelIcon}
          {cancelText}
        </Button>
        <Button
          color={variant === "red" ? "red" : "contrast"}
          size="lg"
          type="submit"
          className={variant === "red" ? "font-medium" : ""}
          disabled={isSubmitting || (checkboxContent ? !isChecked : false)}
          loading={isSubmitting}
        >
          {submitIcon}
          {submitText}
        </Button>
      </ModalActions>
    </ModalLayout>
  );
};
